// src/utils/scheduleVisibility.js
//
// Pass 57A — INSTRUCTOR SCHEDULE VISIBILITY / PUBLISHING POLICY. For SAS-School (organization/internal)
// instructors an admin controls how far into the future the instructor can see assigned work; COLLECTIVE
// instructors are self-managed and exempt. This is the ONE authoritative policy layer — UI, My Schedule,
// notifications, and the recalc writer all call these functions (never re-derive the rule).
//
// Data model (canonical):
//   organization.scheduleVisibilityPolicy = { mode:'rolling'|'full_season', horizonDays:Number }
//   organizationMember.scheduleVisibilityOverride = 'default'|'7'|'14'|'30'|'60'|'90'|'full_season'
//       (lives on the ORG/season relationship — the per-org member doc — never the global human)
//   booking.instructorVisibleFrom = 'YYYY-MM-DD' | null   (null = immediately visible)
//   booking.instructorVisibilityOverride = 'published_early'  (+ publishedToInstructorAt/By)
//
// Visibility is ACCESS POLICY, not decoration (see firestore.rules + useInstructorSchedule enforcement).

import { resolveInstructorCategory, CATEGORY, CURRENT_SEASON_ID } from './instructorCategory';

export const VISIBILITY_PRESETS = [
  { key: '7', label: '1 week', horizonDays: 7 },
  { key: '14', label: '2 weeks', horizonDays: 14 },
  { key: '30', label: '1 month', horizonDays: 30 },
  { key: '60', label: '2 months', horizonDays: 60 },
  { key: '90', label: '3 months', horizonDays: 90 },
  { key: 'full_season', label: 'Full season', horizonDays: null },
];
const PRESET_BY_KEY = Object.fromEntries(VISIBILITY_PRESETS.map((p) => [p.key, p]));

// System default when an organization has NOT configured a policy: full season (everything visible) —
// preserves existing production behaviour; NEVER silently hides a schedule via migration (item 2).
export const SYSTEM_DEFAULT_POLICY = { mode: 'full_season', horizonDays: null };

// Pass 57C-final (item 1) — a RESTRICTED (rolling) policy needs the Pass-57D atomic server endpoint
// that updates the policy AND recomputes every affected booking.instructorVisibleFrom in ONE operation.
// Until that endpoint is deployed we must NOT let the client activate a restricted policy, or we'd have
// policy=7d while bookings still say visibleFrom for 30d (stale). full_season / 'default' are always
// safe (they never hide, so no recompute is required). Flip to true only when 57D deploys.
export const PUBLISHING_SERVICE_DEPLOYED = false;

// Can this visibility choice be committed client-side right now without risking stale booking state?
// full_season and the member 'default' (→ inherits org) are always safe; a rolling horizon is safe
// only once the publishing service is deployed.
export function canCommitVisibilityChoiceClientSide(choiceKey) {
  if (choiceKey === 'full_season' || choiceKey === 'default') return true;
  return PUBLISHING_SERVICE_DEPLOYED === true;
}

const DAY_MS = 86400000;
const toDateKey = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

// Normalise an organization's stored policy into a safe { mode, horizonDays }.
export function resolveOrganizationVisibilityPolicy(organization = {}) {
  const p = organization && organization.scheduleVisibilityPolicy;
  if (!p || !p.mode) return { ...SYSTEM_DEFAULT_POLICY, source: 'system_default' };
  if (p.mode === 'full_season') return { mode: 'full_season', horizonDays: null, source: 'org_default' };
  const horizonDays = Number(p.horizonDays);
  if (!Number.isFinite(horizonDays) || horizonDays <= 0) return { ...SYSTEM_DEFAULT_POLICY, source: 'system_default' };
  return { mode: 'rolling', horizonDays, source: 'org_default' };
}

// THE authoritative effective policy for one instructor at one organization/season.
//   Collective  → { applicable:false, mode:'self_managed' }  (unrestricted, self-managed)
//   School/unclassified with a real override → the override
//   else → the organization default (or system default)
export function effectiveInstructorScheduleVisibility({ organization = {}, member = {}, seasonId = CURRENT_SEASON_ID } = {}) {
  // Season-specific affiliation wins (a person may be School at org A, Collective at org B, and it can
  // vary by season); fall back to the current pool-based category when no season record exists.
  const seasonCat = resolveInstructorCategory(member, { season: seasonId });
  const category = seasonCat !== CATEGORY.UNCLASSIFIED ? seasonCat : resolveInstructorCategory(member, {});
  if (category === CATEGORY.COLLECTIVE) {
    return { applicable: false, mode: 'self_managed', horizonDays: null, source: 'collective', category };
  }
  const override = member.scheduleVisibilityOverride;
  if (override && override !== 'default') {
    if (override === 'full_season') return { applicable: true, mode: 'full_season', horizonDays: null, source: 'member_override', category };
    const preset = PRESET_BY_KEY[String(override)];
    if (preset && preset.horizonDays) return { applicable: true, mode: 'rolling', horizonDays: preset.horizonDays, source: 'member_override', category };
  }
  const org = resolveOrganizationVisibilityPolicy(organization);
  return { applicable: true, mode: org.mode, horizonDays: org.horizonDays, source: org.source, category };
}

// The date from which an assigned booking becomes visible to its instructor. null = immediately visible
// (full season / not applicable). Uses the known serviceDate; TIME-TBD needs no fabricated time (item 5).
export function computeInstructorVisibleFrom(booking = {}, policy = {}) {
  if (!policy || policy.applicable === false || policy.mode === 'full_season' || !policy.horizonDays) return null;
  const serviceDate = String(booking.serviceDate || booking.date || '');
  const m = serviceDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null; // no safe service date → don't hide (conservative)
  const svc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return toDateKey(new Date(svc - policy.horizonDays * DAY_MS));
}

// Epoch-ms form of instructorVisibleFrom (item 5 — a QUERYABLE NUMBER, never null/missing semantics):
//   0            = immediately visible (full season / not applicable / published early)
//   future epoch = the rolling publication moment (JST midnight of serviceDate − horizon)
// The instructor query filters `instructorVisibleFromMs <= now` and the Firestore rule enforces the
// SAME condition, so no hidden future booking is ever downloaded then filtered in React.
export function computeInstructorVisibleFromMs(booking = {}, policy = {}) {
  const key = computeInstructorVisibleFrom(booking, policy);
  if (!key) return 0; // visible now
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y, m - 1, d) - 9 * 3600 * 1000; // JST midnight
}

// Is this booking visible to the instructor right now? Published-early always wins; full-season/
// self-managed always visible; otherwise now >= (stored instructorVisibleFrom or computed).
export function isBookingVisibleToInstructorNow(booking = {}, policy = {}, now = new Date()) {
  if (booking.instructorVisibilityOverride === 'published_early') return true;
  if (!policy || policy.applicable === false || policy.mode === 'full_season' || !policy.horizonDays) return true;
  const from = booking.instructorVisibleFrom != null ? String(booking.instructorVisibleFrom) : computeInstructorVisibleFrom(booking, policy);
  if (!from) return true; // no safe boundary → visible (conservative, never hide via ambiguity)
  return toDateKey(now instanceof Date ? now : new Date(now)) >= from;
}

// Notification classification (item 12): a hidden future assignment stores normally but must NOT notify.
export function classifyAssignmentVisibility(booking = {}, policy = {}, now = new Date()) {
  return isBookingVisibleToInstructorNow(booking, policy, now) ? 'VISIBLE_NOW' : 'ASSIGNED_NOT_YET_PUBLISHED';
}

// Filter a set of the instructor's OWN bookings down to those currently visible (My Schedule enforcement).
export function filterVisibleForInstructor(bookings = [], policy = {}, now = new Date()) {
  return bookings.filter((b) => isBookingVisibleToInstructorNow(b, policy, now));
}

// The furthest date the instructor's schedule is currently published through (for honest UX, item 14).
export function publishedThroughDate(policy = {}, now = new Date()) {
  if (!policy || policy.applicable === false || policy.mode === 'full_season' || !policy.horizonDays) return null; // full season
  const base = now instanceof Date ? now : new Date(now);
  return toDateKey(new Date(base.getTime() + policy.horizonDays * DAY_MS));
}

// Recalc plan (item 9): when a policy changes, recompute instructorVisibleFrom for FUTURE, ACTIVE,
// ASSIGNED bookings of affected instructors. NEVER touches historical/retired/published-early bookings
// or identity/createdAt. `policyForBooking(booking)` returns the effective policy for that booking's
// instructor. Pure — the caller applies via a controlled writer.
export function planVisibilityRecalc(bookings = [], policyForBooking, { now = new Date() } = {}) {
  const nowKey = toDateKey(now instanceof Date ? now : new Date(now));
  const updates = [];
  for (const b of bookings) {
    if (b.active === false || b.lifecycleState === 'retired_superseded') continue;      // never retired
    if (b.instructorVisibilityOverride === 'published_early') continue;                  // preserve early publish
    if (!b.instructorId || String(b.assignmentState || '') === 'unassigned') continue;   // only assigned
    const svc = String(b.serviceDate || b.date || '');
    if (svc && svc < nowKey) continue;                                                   // never historical
    const policy = policyForBooking(b) || {};
    const next = computeInstructorVisibleFrom(b, policy);
    const current = b.instructorVisibleFrom != null ? String(b.instructorVisibleFrom) : null;
    // Persist BOTH the string (UI) and epoch-ms (Firestore rules) so policy + rule agree exactly.
    if ((next || null) !== (current || null)) updates.push({ bookingId: b.id, set: { instructorVisibleFrom: next, instructorVisibleFromMs: computeInstructorVisibleFromMs(b, policy) } });
  }
  return { updates, count: updates.length };
}

// The booking-level "Publish to instructor now" patch — reveals ONLY this booking. Retains provenance
// AND sets instructorVisibleFromMs=0 so it is immediately QUERYABLE/readable by the instructor (item 5).
export function publishEarlyPatch({ actorUserId = '', actorName = '' } = {}) {
  return { instructorVisibilityOverride: 'published_early', instructorVisibleFromMs: 0, publishedToInstructorBy: actorUserId, publishedToInstructorByName: actorName };
}
